import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoMark, Wordmark } from "@/components/Logo";
import { useModals } from "@/components/ModalContext";
import { NAV_LINKS } from "@/constants/site";
import { scrollToSection } from "@/lib/scroll";
import { cn } from "@/lib/utils";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openContact, openSchedule } = useModals();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(href), 400);
    } else {
      scrollToSection(href);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed inset-x-0 top-0 z-40 transition-all duration-500",
        scrolled
          ? "border-b border-arena/50 bg-fondo/90 shadow-[0_10px_36px_rgba(58,36,24,0.07)] backdrop-blur-md"
          : "bg-transparent"
      )}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12">
        <button
          data-testid="header-logo"
          onClick={() => go("#inicio")}
          className="flex items-center gap-3"
          aria-label="Ir al inicio"
        >
          <LogoMark className="h-10 w-10" />
          <Wordmark />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegación principal">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="group relative text-sm font-medium text-tinta/70 transition-colors hover:text-tinta"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-chocolate transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Button
            variant="outline"
            data-testid="header-contact-button"
            onClick={() => openContact()}
            className="rounded-full px-6"
          >
            Contactar
          </Button>
          <Button
            data-testid="header-schedule-button"
            onClick={openSchedule}
            className="rounded-full px-6"
          >
            Agendar llamada
          </Button>
        </div>

        <button
          data-testid="header-hamburger-button"
          onClick={() => setOpen(true)}
          className="rounded-full p-2 transition-colors hover:bg-arena/50 lg:hidden"
          aria-label="Abrir menú"
        >
          <Menu className="h-6 w-6 text-tinta" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="header-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex h-screen flex-col bg-fondo px-6 py-5 lg:hidden"
          >
            <div className="flex h-[52px] items-center justify-between">
              <div className="flex items-center gap-3">
                <LogoMark className="h-9 w-9" />
                <Wordmark />
              </div>
              <button
                data-testid="header-mobile-close-button"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 transition-colors hover:bg-arena/50"
                aria-label="Cerrar menú"
              >
                <X className="h-6 w-6 text-tinta" />
              </button>
            </div>
            <nav className="mt-12 flex flex-col gap-6" aria-label="Navegación móvil">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  className="text-left font-serif text-3xl text-tinta"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-3 pb-6">
              <Button
                variant="outline"
                data-testid="mobile-contact-button"
                onClick={() => {
                  setOpen(false);
                  openContact();
                }}
                className="w-full rounded-full py-6"
              >
                Contactar
              </Button>
              <Button
                data-testid="mobile-schedule-button"
                onClick={() => {
                  setOpen(false);
                  openSchedule();
                }}
                className="w-full rounded-full py-6"
              >
                Agendar llamada
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
